import type {
  Qualification as ApiQualification,
  Reseller as ApiReseller,
  SalesStatistic as ApiSalesStatistic,
} from "randmar-api-client";
import { OrderDetail } from "../document/Document";
import { SalesData } from "../manufacturer/Manufacturer";
import { Opportunity } from "../opportunities/Opportunity";
import Return from "../return/Return";

export default class Reseller {
  arEmail?: string;
  address1?: string;
  address2?: string;
  autoUpdate?: boolean;
  city?: string;
  contactName?: string;
  createdDate?: number;
  creditLimit?: number;
  defaultWarehouse?: string;
  defaultLeadTime?: string;
  defaultMaxInventoryValue?: string;
  defaultMinimumOrder?: string;
  defaultWeeksOfStocking?: string;
  domain?: string;
  googleEmail?: string;
  resellerId?: string;
  name?: string;
  pOEmail?: string;
  phone?: string;
  postalCode?: string;
  province?: string;
  country?: string;
  publicName?: string;
  publicEmail?: string;
  shopifyWebsite?: string;
  terms?: string
  website?: string;
  priority?: string;
  shippingProfile?: string;
  priceProfile?: number;
  isQualified?: boolean;
  onHold?: number;
  creationDate?: number;
  lastInvoiceDate?: number;
  salesData?: SalesData;
  salesStatistics?: ApiSalesStatistic[];
  activeOrderDetails?: OrderDetail[];
  completedOrderDetails?: OrderDetail[];

  constructor(source?: ApiReseller) {
    if (source) {
      this.address1 = source.Address1 ?? undefined;
      this.address2 = source.Address2 ?? undefined;
      this.city = source.City ?? undefined;
      this.contactName = source.ContactName ?? undefined;
      this.country = source.Country ?? undefined;
      this.createdDate = source.CreationDate;
      this.creditLimit = source.CreditLimit;
      this.defaultWarehouse = source.DefaultWarehouse ?? undefined;
      this.domain = source.Domain ?? undefined;
      this.resellerId = source.ResellerId ?? undefined;
      this.name = source.Name ?? undefined;
      this.pOEmail = source.POEmail ?? undefined;
      this.phone = source.Phone ?? undefined;
      this.postalCode = source.PostalCode ?? undefined;
      this.province = source.Province ?? undefined;
      this.publicName = (source.PublicName || source.Name) ?? undefined;
      this.publicEmail = source.PublicEmail ?? undefined;
      this.terms = source.Terms ?? undefined;
      this.website = source.Website ?? undefined;
      this.priority = source.Priority ?? undefined;
      this.shippingProfile = source.ShippingProfile ?? undefined;
      this.priceProfile = source.PriceProfile;
      this.isQualified = source.IsQualified;
      this.onHold = source.OnHold;
      this.creationDate = source.CreationDate;
      this.lastInvoiceDate = source.LastInvoiceDate;

      if (source.Qualifications) {
        const manufacturerNameById = new Map<string, string>();
        for (const manufacturer of source.QualifiedManufacturers ?? []) {
          const manufacturerId = manufacturer.ManufacturerId ?? "";
          if (!manufacturerId) continue;
          manufacturerNameById.set(
            manufacturerId,
            (manufacturer.PublicName || manufacturer.Name || manufacturerId) ?? manufacturerId
          );
        }

        this.qualifications = source.Qualifications.map((q) => new ResellerQualification(q, manufacturerNameById));
      }

      if (source.Returns) this.returns = source.Returns.map(Return.convertFrom);
      if (source.Opportunities) this.opportunities = source.Opportunities.map(Opportunity.convertFrom);
      if (source.SalesData) this.salesData = SalesData.convertFrom(source.SalesData);
      if (source.SalesStatistics) this.salesStatistics = source.SalesStatistics;
      if (source.ActiveOrderDetails) this.activeOrderDetails = source.ActiveOrderDetails.map(OrderDetail.convertFromApi);
      if (source.CompletedOrderDetails) this.completedOrderDetails = source.CompletedOrderDetails.map(OrderDetail.convertFromApi);
    }
  }

  /**
   * @deprecated Use the `priceProfileLetter` getter instead.
   */
  public PriceProfile(): string {
    return this.priceProfileLetter;
  }

  public get priceProfileLetter(): string {
    return String.fromCharCode((this.priceProfile || 3) + 64);
  }

  public qualifications?: ResellerQualification[];
  public returns?: Return[];
  public opportunities?: Opportunity[];

  public static convertFrom(source: ApiReseller): Reseller {
    return new Reseller(source);
  }
}

export class ResellerQualification {
  manufacturerId?: string;
  qualificationId?: string;
  manufacturerPublicName?: string;

  constructor(source: ApiQualification, manufacturerNameById?: Map<string, string>) {
    this.manufacturerId = source.ManufacturerId ?? undefined;
    this.qualificationId = source.QualificationId ?? undefined;
    this.manufacturerPublicName =
      (this.manufacturerId ? manufacturerNameById?.get(this.manufacturerId) : undefined) ?? undefined;
  }
}

export class SalesStatistics {
  day?: number;
  quantity?: number;
  extendedPrice?: number;

  public static convertFrom(source: ApiSalesStatistic): SalesStatistics {
    const salesStatistics = new SalesStatistics();

    salesStatistics.day = source.Day;
    salesStatistics.quantity = source.Quantity;
    salesStatistics.extendedPrice = source.ExtendedPrice;

    return salesStatistics;
  }
}
